import { apiClient } from './authService';
import parameterLabels from '../config/parameterLabels.json';

const normalizeKey = (key) => {
  if (!key) return '';
  return key
    .replace(/_/g, ' ')
    .replace(/\b([a-z])/g, (match) => match.toUpperCase())
    .trim();
};

const getParameterMetadata = (parameterId) => parameterLabels[parameterId] || null;

const buildParameterLabel = (parameterId, fallback) => {
  const override = getParameterMetadata(parameterId);
  return override?.label ?? fallback;
};

const buildParameterDescription = (parameterId, ...fallbacks) => {
  const override = getParameterMetadata(parameterId);
  if (override?.description) {
    return override.description;
  }
  return fallbacks.find((value) => Boolean(value)) ?? null;
};

const inferControlType = (spec) => {
  if (!spec) {
    return 'text';
  }

  if (Array.isArray(spec.allowed) || Array.isArray(spec.values)) {
    return 'select';
  }

  const value = spec.value ?? spec.default;
  if (typeof value === 'boolean') {
    return 'switch';
  }

  if (typeof value === 'number' || typeof spec.min === 'number' || typeof spec.max === 'number') {
    return 'number';
  }

  return 'text';
};

// v2-aware: hide when not available and no toggle
const shouldHideSection = (section) => {
  if (!section || typeof section !== 'object') return false;

  if (typeof section.available === 'boolean' || typeof section.show_toggle === 'boolean') {
    return section.available === false && section.show_toggle !== true;
  }

  // v1 fallback (enabled gate)
  const enabled = section.enabled;
  if (!enabled || typeof enabled !== 'object') return false;
  const val = typeof enabled.value === 'boolean' ? enabled.value : (typeof enabled.default === 'boolean' ? enabled.default : true);
  return val === false && enabled.expose !== true;
};

const normalizeControl = (groupId, controlId, spec) => {
  const controlKey = `${groupId}.${controlId}`;
  const options = spec?.allowed || spec?.values;

  const mapOption = (option) => {
    if (typeof option === 'object') {
      const optionValue = option.value ?? option;
      const optionKey = `${controlKey}.option.${optionValue}`;
      const override = getParameterMetadata(optionKey);
      return {
        ...option,
        value: optionValue,
        label: override?.label ?? option.label ?? normalizeKey(String(optionValue)),
        description: override?.description ?? option.description,
      };
    }

    const optionValue = option;
    const optionKey = `${controlKey}.option.${optionValue}`;
    const override = getParameterMetadata(optionKey);
    return {
      label: override?.label ?? normalizeKey(String(optionValue)),
      value: optionValue,
    };
  };

  return {
    id: controlKey,
    name: controlId,
    label: buildParameterLabel(controlKey, normalizeKey(controlId)),
    controlType: inferControlType(spec),
    helpText: buildParameterDescription(controlKey, spec?.description, spec?.hint),
    defaultValue: spec?.value ?? spec?.default ?? null,
    min: spec?.min ?? null,
    max: spec?.max ?? null,
    step: spec?.step ?? null,
    nullable: spec?.nullable === true,
    options: Array.isArray(options) ? options.map(mapOption) : [],
    editable: spec?.locked ? false : true,
  };
};

const metaKeys = new Set([
  'requires',
  'enabled',
  'available',
  'show_toggle',
  'default_enabled',
  'locked',
  'visible',
  'enabled_by_default',
  'description',
  'pipeline_step_order',
  'texture_upload',
  'algorithms',
]);

const shouldIncludeControl = (key, spec) => {
  if (metaKeys.has(key)) {
    return false;
  }
  if (spec == null || typeof spec !== 'object' || Array.isArray(spec)) {
    return false;
  }
  if (spec.expose === false) {
    return false;
  }
  return true;
};

const buildControls = (groupId, section) => {
  const controls = [];
  Object.entries(section || {}).forEach(([key, value]) => {
    if (!shouldIncludeControl(key, value)) {
      return;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      controls.push(normalizeControl(groupId, key, value));
    }
  });
  return controls;
};

const createToggleControl = (groupId, section, label = 'Enabled') => {
  if (!section || section.show_toggle !== true) {
    return [];
  }
  const controlId = '__enabled';
  const controlKey = `${groupId}.${controlId}`;
  return [
    {
      id: controlKey,
      name: controlId,
      label: buildParameterLabel(controlKey, label),
      controlType: 'switch',
      defaultValue: section.default_enabled === false ? false : true,
      helpText: buildParameterDescription(controlKey, section?.description),
      editable: section.locked ? false : true,
    },
  ];
};

const makeGroup = (sectionId, section, title, controls) => {
  const parts = sectionId.split('.');
  const rootId = parts[0];
  const parentId = parts.length > 1 ? parts.slice(0, -1).join('.') : null;
  let kind = 'section';
  if (sectionId.includes('.algorithms.')) {
    kind = 'algorithm';
  } else if (sectionId.endsWith('texture_upload')) {
    kind = 'texture';
  }

  const group = {
    id: sectionId,
    title,
    description: section?.description || '',
    controls,
    kind,
    rootId,
    parentId,
    meta: {
      available: section?.available,
      show_toggle: section?.show_toggle,
      default_enabled: section?.default_enabled,
      locked: section?.locked,
      visible: section?.visible,
    },
  };

  if (kind === 'algorithm') {
    group.meta.algorithmId = parts[parts.length - 1];
    const algorithmKey = `${rootId}.algorithm.option.${group.meta.algorithmId}`;
    const algorithmLabel = buildParameterLabel(
      algorithmKey,
      normalizeKey(group.meta.algorithmId)
    );
    group.meta.label = algorithmLabel;
    const baseTitle = title.includes('›') ? title.split('›')[0].trim() : title.trim();
    group.title = `${baseTitle} › ${algorithmLabel}`;
  }

  if (kind === 'section' && section?.algorithm) {
    const optionValues = section.algorithm.allowed || [];
    const optionLabels = optionValues.reduce((acc, option) => {
      acc[option] = buildParameterLabel(
        `${sectionId}.algorithm.option.${option}`,
        buildParameterLabel(`${rootId}.algorithm.option.${option}`, normalizeKey(option))
      );
      return acc;
    }, {});

    group.meta.algorithm = {
      options: optionValues,
      optionLabels,
      default: section.algorithm.default ?? section.algorithm.allowed?.[0] ?? null,
      locked: section.algorithm.locked ?? false,
    };
  }

  return group;
};

const normalizeSectionGroups = (sectionId, section, titleOverride) => {
  if (!section || typeof section !== 'object') {
    return [];
  }

  if (section.visible === false) {
    return [];
  }

  const groups = [];
  const baseId = sectionId;
  const title = titleOverride || normalizeKey(sectionId.split('.').pop());

  const baseControls = [
    ...createToggleControl(baseId, section),
    ...buildControls(baseId, section),
  ];

  if (!shouldHideSection(section) && baseControls.length > 0) {
    groups.push(makeGroup(baseId, section, title, baseControls));
  }

  if (section.texture_upload && typeof section.texture_upload === 'object') {
    groups.push(
      ...normalizeSectionGroups(
        `${sectionId}.texture_upload`,
        section.texture_upload,
        `${title} › Texture Upload`
      )
    );
  }

  if (section.algorithms && typeof section.algorithms === 'object') {
    Object.entries(section.algorithms).forEach(([algorithmId, algorithmSpec]) => {
      const algorithmLabel = buildParameterLabel(
        `${sectionId}.algorithm.option.${algorithmId}`,
        buildParameterLabel(`${baseId}.algorithm.option.${algorithmId}`, normalizeKey(algorithmId))
      );
      groups.push(
        ...normalizeSectionGroups(
          `${sectionId}.algorithms.${algorithmId}`,
          algorithmSpec,
          `${title} › ${algorithmLabel}`
        )
      );
    });
  }

  return groups;
};

const normalizeResponse = (data) => {
  if (!data?.sections) {
    return [];
  }

  const groups = [];
  Object.entries(data.sections).forEach(([sectionId, section]) => {
    groups.push(...normalizeSectionGroups(sectionId, section));
  });

  return groups;
};

export const parameterService = {
  async getUserParameters() {
    const response = await apiClient.get('/parameters/user/allowed-parameters');
    return {
      raw: response.data,
      groups: normalizeResponse(response.data),
    };
  },

  async listUserPresets(scope) {
    const res = await apiClient.get('/parameters/user/presets', {
      params: scope ? { scope } : {},
    });
    return res.data || [];
  },

  async createUserPreset(payload) {
    const res = await apiClient.post('/parameters/user/presets', payload);
    return res.data;
  },

  async updateUserPreset(id, payload) {
    const res = await apiClient.put(`/parameters/user/presets/${id}`, payload);
    return res.data;
  },

  async deleteUserPreset(id) {
    await apiClient.delete(`/parameters/user/presets/${id}`);
  },
};
